<ng-container *transloco="let t">
  <div class="mx-auto max-w-[1280px]">
    @if (loading()) {
      <!-- Skeleton: mirrors the sidebar-card layout geometry. -->
      <p class="sr-only" role="status">{{ t('tenantSettings.loading') }}</p>
      <div class="overflow-hidden rounded-card bg-card shadow-hairline" aria-hidden="true">
        <div class="grid grid-cols-[248px_1fr]">
          <div class="flex flex-col gap-3 border-r border-hairline px-3 py-6">
            <div class="h-3 w-20 animate-pulse rounded bg-surface-2"></div>
            <div class="h-4 w-28 animate-pulse rounded bg-surface-2"></div>
            @for (bone of [0, 1, 2, 3]; track bone) {
              <div class="mt-1 h-10 animate-pulse rounded-lg bg-surface-2"></div>
            }
          </div>
          <div class="flex flex-col gap-6 p-8">
            @for (card of [0, 1]; track card) {
              <div>
                <div class="h-4 w-36 animate-pulse rounded bg-surface-2"></div>
                @for (row of [0, 1, 2]; track row) {
                  <div class="mt-5 flex items-center justify-between gap-6">
                    <div class="h-3.5 w-44 animate-pulse rounded bg-surface-2"></div>
                    <div class="h-8 w-52 animate-pulse rounded-control bg-surface-2"></div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    } @else if (loadFailed()) {
      <div class="rounded-card bg-card p-6 shadow-hairline">
        <p class="text-sm text-ink-2">{{ t('tenantSettings.loadFailedBody') }}</p>
        <app-button class="mt-3" size="sm" [label]="t('tenantSettings.retry')" (activated)="retry()" />
      </div>
    } @else if (form(); as f) {
      <!-- Phone tabs: shown only on small screens above the content. -->
      <nav
        class="mb-3 flex flex-none items-center gap-1 overflow-x-auto border-b border-hairline md:hidden"
        [attr.aria-label]="t('tenantSettings.title')"
      >
        @for (id of sections; track id) {
          <button
            type="button"
            (click)="goTo(id)"
            class="relative flex-none cursor-pointer whitespace-nowrap px-3 py-2.5 font-display text-[12px] font-bold uppercase leading-4 tracking-[0.03em] transition-colors"
            [class]="activeSection() === id ? 'text-ink-1' : 'text-ink-2 hover:text-ink-1'"
            [attr.aria-current]="activeSection() === id ? 'true' : null"
          >
            {{ t('tenantSettings.nav.' + id) }}
            @if (activeSection() === id) {
              <span class="absolute inset-x-1 bottom-0 h-1 bg-action"></span>
            }
          </button>
        }
      </nav>

      <!-- Unified settings card: sidebar nav + scrollable content area. -->
      <div class="rounded-card bg-card shadow-hairline">
        <div class="grid grid-cols-1 md:grid-cols-[248px_1fr]">

          <!-- Left nav panel -->
          <nav
            class="hidden flex-col gap-7 border-r border-hairline px-3 py-6 md:flex md:sticky md:top-0 md:max-h-screen md:self-start md:overflow-y-auto"
            [attr.aria-label]="t('tenantSettings.title')"
          >
            <!-- Group header: label + entity name -->
            <div class="flex flex-col gap-2 px-3">
              <span class="font-display text-[12px] font-bold uppercase leading-4 tracking-[0.03em] text-ink-2">
                {{ t('tenantSettings.orgLabel') }}
              </span>
              <span class="text-[14px] font-semibold leading-5 text-ink-1">{{ tenantName() }}</span>
            </div>
            <!-- Nav items -->
            <div class="flex flex-col gap-0.5">
              @for (id of sections; track id) {
                <button
                  type="button"
                  (click)="goTo(id)"
                  class="flex h-10 cursor-pointer items-center rounded-lg px-3 text-left font-body text-[14px] leading-5 transition-colors"
                  [class]="activeSection() === id
                    ? 'bg-surface-2 font-semibold text-ink-1'
                    : 'font-normal text-ink-1 hover:bg-surface-2'"
                  [attr.aria-current]="activeSection() === id ? 'true' : null"
                >
                  {{ t('tenantSettings.nav.' + id) }}
                </button>
              }
            </div>
          </nav>

          <!-- Right scrollable content -->
          <div class="flex min-w-0 flex-col gap-0 divide-y divide-hairline">
            <!-- Page header inside the card -->
            <div class="flex items-start justify-between gap-4 px-8 py-6">
              <div class="flex flex-col gap-1">
                <h1 class="font-display text-2xl font-bold leading-8 text-ink-1">
                  {{ tenantName() }} {{ t('tenantSettings.orgSuffix') }}
                </h1>
                <p class="text-sm leading-5 text-ink-2">{{ t('tenantSettings.subtitle') }}</p>
              </div>
              @if (readOnly()) {
                <div class="mt-1 inline-flex h-7 shrink-0 items-center rounded-pill bg-info-surface px-3 text-xs font-semibold text-brand">
                  {{ t('tenantSettings.readOnly', { role: t(roleLabelKey()) }) }}
                </div>
              }
            </div>

            <!-- Settings sections -->
            <div class="flex flex-col divide-y divide-hairline px-8 pb-16">

              <!-- Alerts -->
              <app-setting-section
                id="sec-alerts"
                class="scroll-mt-4 py-6"
                [flat]="true"
                [title]="t('tenantSettings.alerts.title')"
                [subtitle]="t('tenantSettings.alerts.subtitle')"
                [dirty]="dirty().alerts"
                [saved]="!!savedNotes().alerts"
                [saving]="savingSection() === 'alerts'"
                (save)="saveSection('alerts')"
                (discard)="discardSection('alerts')"
              >
                <app-setting-row
                  [label]="t('tenantSettings.alerts.enabled.label')"
                  [defaultLabel]="captions()?.alerts ?? ''"
                  [showReset]="differs('alertsEnabled', 'chEmail', 'chWebhook', 'recipients', 'webhookUrl')"
                  (reset)="resetToDefault('alertsEnabled', 'chEmail', 'chWebhook', 'recipients', 'webhookUrl')"
                >
                  <app-toggle
                    [ngModel]="f.alertsEnabled"
                    (ngModelChange)="patch('alertsEnabled', $event)"
                    [disabled]="readOnly()"
                    [attr.aria-label]="t('tenantSettings.alerts.enabled.label')"
                  />
                </app-setting-row>
                <app-setting-row
                  [label]="t('tenantSettings.alerts.channels.label')"
                  [help]="t('tenantSettings.alerts.channels.help')"
                >
                  <app-checkbox
                    [ngModel]="f.chEmail"
                    (ngModelChange)="patch('chEmail', $event)"
                    [disabled]="channelsDisabled()"
                    [label]="t('tenantSettings.alerts.channels.email')"
                  />
                  <app-checkbox
                    class="ml-3"
                    [ngModel]="f.chWebhook"
                    (ngModelChange)="patch('chWebhook', $event)"
                    [disabled]="channelsDisabled()"
                    [label]="t('tenantSettings.alerts.channels.webhook')"
                  />
                </app-setting-row>
                <app-setting-row
                  [label]="t('tenantSettings.alerts.recipients.label')"
                  [help]="t('tenantSettings.alerts.recipients.help')"
                >
                  <app-text-input
                    class="block w-[280px] max-w-full"
                    size="sm"
                    type="email"
                    [ngModel]="f.recipients"
                    (ngModelChange)="patch('recipients', $event)"
                    [disabled]="recipientsDisabled()"
                    [placeholder]="t('tenantSettings.alerts.recipients.placeholder')"
                  />
                </app-setting-row>
                <app-setting-row
                  [label]="t('tenantSettings.alerts.webhookUrl.label')"
                  [help]="t('tenantSettings.alerts.webhookUrl.help')"
                >
                  <app-text-input
                    class="block w-[280px] max-w-full"
                    size="sm"
                    [ngModel]="f.webhookUrl"
                    (ngModelChange)="patch('webhookUrl', $event)"
                    [disabled]="webhookDisabled()"
                    [placeholder]="t('tenantSettings.alerts.webhookUrl.placeholder')"
                  />
                </app-setting-row>
              </app-setting-section>

              <!-- Data retention -->
              <app-setting-section
                id="sec-retention"
                class="scroll-mt-4 py-6"
                [flat]="true"
                [title]="t('tenantSettings.retention.title')"
                [subtitle]="t('tenantSettings.retention.subtitle')"
                [dirty]="dirty().retention"
                [saved]="!!savedNotes().retention"
                [canSave]="!invalid().retention"
                [saving]="savingSection() === 'retention'"
                (save)="saveSection('retention')"
                (discard)="discardSection('retention')"
              >
                <app-setting-row
                  [label]="t('tenantSettings.retention.logs.label')"
                  [help]="t('tenantSettings.retention.logs.help')"
                  [defaultLabel]="captions()?.logsDays ?? ''"
                  [showReset]="differs('logsDays')"
                  (reset)="resetToDefault('logsDays')"
                >
                  <app-number-input
                    class="block w-full max-w-52"
                    size="sm"
                    [ngModel]="f.logsDays"
                    (ngModelChange)="patch('logsDays', $event)"
                    [disabled]="readOnly()"
                    [min]="1"
                    [stepper]="true"
                    [invalid]="fieldInvalid().logsDays"
                    [unit]="t('tenantSettings.units.days')"
                  />
                </app-setting-row>
                <app-setting-row
                  [label]="t('tenantSettings.retention.snapshots.label')"
                  [help]="t('tenantSettings.retention.snapshots.help')"
                  [defaultLabel]="captions()?.snapshotsDays ?? ''"
                  [showReset]="differs('snapshotsDays')"
                  (reset)="resetToDefault('snapshotsDays')"
                >
                  <app-number-input
                    class="block w-full max-w-52"
                    size="sm"
                    [ngModel]="f.snapshotsDays"
                    (ngModelChange)="patch('snapshotsDays', $event)"
                    [disabled]="readOnly()"
                    [min]="1"
                    [stepper]="true"
                    [invalid]="fieldInvalid().snapshotsDays"
                    [unit]="t('tenantSettings.units.days')"
                  />
                </app-setting-row>
              </app-setting-section>

              <!-- Weather -->
              <app-setting-section
                id="sec-weather"
                class="scroll-mt-4 py-6"
                [flat]="true"
                [title]="t('tenantSettings.weather.title')"
                [subtitle]="t('tenantSettings.weather.subtitle')"
                [dirty]="dirty().weather"
                [saved]="!!savedNotes().weather"
                [saving]="savingSection() === 'weather'"
                (save)="saveSection('weather')"
                (discard)="discardSection('weather')"
              >
                <app-setting-row
                  [label]="t('tenantSettings.weather.location.label')"
                  [help]="t('tenantSettings.weather.location.help')"
                  [defaultLabel]="captions()?.weatherLocation ?? ''"
                  [showReset]="differs('weatherLocation')"
                  (reset)="resetToDefault('weatherLocation')"
                >
                  <app-text-input
                    class="block w-[280px] max-w-full"
                    size="sm"
                    [ngModel]="f.weatherLocation"
                    (ngModelChange)="patch('weatherLocation', $event)"
                    [disabled]="readOnly()"
                    [placeholder]="t('tenantSettings.weather.location.placeholder')"
                  />
                </app-setting-row>
                <app-setting-row
                  [label]="t('tenantSettings.weather.units.label')"
                  [help]="t('tenantSettings.weather.units.help')"
                  [defaultLabel]="captions()?.weatherUnits ?? ''"
                  [showReset]="differs('weatherUnits')"
                  (reset)="resetToDefault('weatherUnits')"
                >
                  <app-segmented-control
                    [ngModel]="f.weatherUnits"
                    (ngModelChange)="patch('weatherUnits', $event)"
                    [disabled]="readOnly()"
                    [segments]="unitSegments()"
                  />
                </app-setting-row>
              </app-setting-section>

              <!-- User defaults -->
              <app-setting-section
                id="sec-userDefaults"
                class="scroll-mt-4 py-6"
                [flat]="true"
                [title]="t('tenantSettings.userDefaults.title')"
                [subtitle]="t('tenantSettings.userDefaults.subtitle')"
                [dirty]="dirty().userDefaults"
                [saved]="!!savedNotes().userDefaults"
                [saving]="savingSection() === 'userDefaults'"
                (save)="saveSection('userDefaults')"
                (discard)="discardSection('userDefaults')"
              >
                <app-setting-row
                  [label]="t('tenantSettings.userDefaults.locale.label')"
                  [help]="t('tenantSettings.userDefaults.locale.help')"
                  [defaultLabel]="captions()?.locale ?? ''"
                  [showReset]="differs('locale')"
                  (reset)="resetToDefault('locale')"
                >
                  <app-select
                    class="block w-60"
                    [ngModel]="f.locale"
                    (ngModelChange)="patch('locale', $event)"
                    [disabled]="readOnly()"
                    [options]="localeOptions"
                  />
                </app-setting-row>
                <app-setting-row
                  [label]="t('tenantSettings.userDefaults.timezone.label')"
                  [help]="t('tenantSettings.userDefaults.timezone.help')"
                  [defaultLabel]="captions()?.timezone ?? ''"
                  [showReset]="differs('timezone')"
                  (reset)="resetToDefault('timezone')"
                >
                  <app-select
                    class="block w-60"
                    [ngModel]="f.timezone"
                    (ngModelChange)="patch('timezone', $event)"
                    [disabled]="readOnly()"
                    [options]="timezoneOptions"
                  />
                </app-setting-row>
              </app-setting-section>
            </div>
            <div class="h-[70vh]"></div>
          </div>
        </div>
      </div>
    }
  </div>
</ng-container>
